import React, { memo, useMemo } from "react";

import {
  AnalyzeTinybirdResourceResponseBodyType,
  CustomEventDto,
  GraphConfig,
} from "@intelligems/ig-types";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import { BaseMetricSection } from "@src/features/analytics/baseDashboard/BaseMetricSection";
import { InsufficientCogsAlert } from "@src/features/analytics/baseDashboard/InsufficientCogsAlert";
import { NoCogsSectionPlaceholder } from "@src/features/analytics/baseDashboard/NoCogsSectionPlaceholder";
import { MetricDetailsWrapper } from "@src/features/analytics/metricDetails/MetricDetailsWrapper";
import { AnalyticsAudienceTableWrapper } from "@src/features/analytics/tables/analyticsAudienceTable/AnalyticsAudienceTableWrapper";
import { ProfitAndLossTable } from "@src/features/analytics/tables/profitAndLossTable/ProfitAndLossTable";
import {
  shouldShowInsufficientCogsWarning,
  shouldShowProfitData,
} from "@src/features/analytics/utils";
import { useSelector } from "@src/store";
import { selectedExperience } from "@src/store/slices/experiences";
import { selectedVariations } from "@src/store/slices/variations";
import { UseQueryResult } from "@tanstack/react-query";

interface ProfitAnalyticsPageProps {
  data: UseQueryResult<AnalyzeTinybirdResourceResponseBodyType>[];
  totalExperienceVisitors: number;
  graphConfigs: GraphConfig[];
  testDuration: number;
  compareVariationId: string;
}

export const ProfitAnalyticsPage = memo(
  ({
    data,
    graphConfigs,
    totalExperienceVisitors,
    testDuration,
    compareVariationId,
  }: ProfitAnalyticsPageProps) => {
    const experience = useSelector(selectedExperience);
    const variations = useSelector(selectedVariations);

    const shouldShowProfit = useMemo(() => {
      if (data[0].isLoading || data[0].isPending) {
        return true;
      }
      const foundDatasetIndex = data.findIndex(
        (data) => data.data?.datasetId === "variation_overview"
      );
      return shouldShowProfitData(
        data[foundDatasetIndex ?? 0]?.data?.metrics || []
      );
    }, [data]);

    if (!shouldShowProfit) {
      return <NoCogsSectionPlaceholder />;
    }

    return (
      <Stack direction="column" spacing={4}>
        {shouldShowInsufficientCogsWarning(data) && (
          <Grid>
            <InsufficientCogsAlert />
          </Grid>
        )}
        <Grid size={12}>
          <BaseMetricSection groupTitle={"Profit & Loss"}>
            <Grid sx={{ width: "100%" }} size={12}>
              <ProfitAndLossTable variationOverviewData={data} />
            </Grid>
          </BaseMetricSection>
        </Grid>
        <Grid>
          <MetricDetailsWrapper
            graphConfigs={graphConfigs}
            dropdownOptions={graphConfigs.map((config) => ({
              title: config.title[0],
              id: config.metrics[0],
            }))}
            variationOverviewData={data}
          />
        </Grid>
        <Grid>
          <AnalyticsAudienceTableWrapper
            experience={experience}
            variations={variations}
            dashboardData={data}
            graphConfigs={graphConfigs.filter((config) => config.hasAudience)}
            testDuration={testDuration}
          />
        </Grid>
      </Stack>
    );
  }
);
